// Estado visible de una promo de venta y qué acciones admite, según el diagrama de estados de REQ-01.
import { LIFECYCLE_STATUS_LABEL } from '@/shared/patterns/Lifecycle';
import type { SalePromotionListItem, SalePromotionTransition } from '../api/promotionsContract';
import { isSystemPromotion } from './systemPromotion';

export type PromotionDisplayStatus =
    | 'DRAFT'
    | 'ACTIVE'
    | 'SCHEDULED'
    | 'PAUSED'
    | 'EXPIRED'
    | 'ARCHIVED';

export const DISPLAY_STATUS_LABEL: Record<PromotionDisplayStatus, string> = {
    DRAFT: LIFECYCLE_STATUS_LABEL.DRAFT,
    ACTIVE: LIFECYCLE_STATUS_LABEL.ACTIVE,
    SCHEDULED: LIFECYCLE_STATUS_LABEL.SCHEDULED,
    PAUSED: LIFECYCLE_STATUS_LABEL.PAUSED,
    EXPIRED: LIFECYCLE_STATUS_LABEL.EXPIRED,
    ARCHIVED: LIFECYCLE_STATUS_LABEL.ARCHIVED,
};

export interface PromotionActionOption {
    transition: SalePromotionTransition;
    label: string;
    needsConfirmation: boolean;
    destructive: boolean;
    /** Si viene, la acción se muestra apagada con este motivo. */
    disabledReason?: string;
}

const BUDGET_EXHAUSTED_REASON = 'presupuesto agotado';

function hasEnded(promo: SalePromotionListItem, now: Date): boolean {
    return promo.validUntil !== null && Date.parse(promo.validUntil) <= now.getTime();
}

/** Programada sale de `phase`; la Vencida por fecha se deriva porque el barrido puede ir atrás del reloj. */
export function displayStatusOf(promo: SalePromotionListItem, now: Date): PromotionDisplayStatus {
    switch (promo.status) {
        case 'DRAFT':
        case 'ARCHIVED':
        case 'EXPIRED':
            return promo.status;
        case 'PAUSED':
            return hasEnded(promo, now) ? 'EXPIRED' : 'PAUSED';
        case 'ACTIVE':
            if (hasEnded(promo, now)) return 'EXPIRED';
            return promo.phase === 'SCHEDULED' ? 'SCHEDULED' : 'ACTIVE';
    }
}

export function isPermanent(promo: SalePromotionListItem): boolean {
    return promo.validUntil === null && promo.status !== 'DRAFT';
}

function isBudgetExhausted(promo: SalePromotionListItem): boolean {
    return promo.budgetMaxCents !== null && promo.budgetConsumedCents >= promo.budgetMaxCents;
}

const PUBLISH: PromotionActionOption = {
    transition: 'PUBLISH',
    label: 'Publicar',
    needsConfirmation: true,
    destructive: false,
};
const PAUSE: PromotionActionOption = {
    transition: 'PAUSE',
    label: 'Pausar',
    needsConfirmation: false,
    destructive: false,
};
const EXPIRE: PromotionActionOption = {
    transition: 'EXPIRE',
    label: 'Vencer ahora',
    needsConfirmation: true,
    destructive: true,
};
const ARCHIVE: PromotionActionOption = {
    transition: 'ARCHIVE',
    label: 'Archivar',
    needsConfirmation: true,
    destructive: true,
};

function resumeOf(promo: SalePromotionListItem): PromotionActionOption {
    return {
        transition: 'RESUME',
        label: 'Reanudar',
        needsConfirmation: false,
        destructive: false,
        ...(isBudgetExhausted(promo) ? { disabledReason: BUDGET_EXHAUSTED_REASON } : {}),
    };
}

/** Una promo de sistema solo se pausa y se reanuda. */
function systemActions(promo: SalePromotionListItem, now: Date): PromotionActionOption[] {
    switch (displayStatusOf(promo, now)) {
        case 'ACTIVE':
        case 'SCHEDULED':
            return [PAUSE];
        case 'PAUSED':
            return [resumeOf(promo)];
        default:
            return [];
    }
}

/** Vencer no aplica a una Permanente: no tiene fin que adelantar. */
export function availableActions(promo: SalePromotionListItem, now: Date): PromotionActionOption[] {
    if (isSystemPromotion(promo)) return systemActions(promo, now);
    const canExpire = promo.validUntil !== null;
    switch (displayStatusOf(promo, now)) {
        case 'ACTIVE':
        case 'SCHEDULED':
            return canExpire ? [PAUSE, EXPIRE, ARCHIVE] : [PAUSE, ARCHIVE];
        case 'PAUSED':
            return canExpire ? [resumeOf(promo), EXPIRE, ARCHIVE] : [resumeOf(promo), ARCHIVE];
        case 'DRAFT':
            return [PUBLISH, ARCHIVE];
        case 'EXPIRED':
            return [ARCHIVE];
        case 'ARCHIVED':
            return [];
    }
}
